import type { WebinarRoomVideoState } from '../types'

/**
 * What one player plays: the session's live stream, or a video on demand (the replay, the rehearsal, a preview, the
 * holding slate's waiting video).
 */
export type RoomPlayerSource =
    | {
        kind: 'live'
        playbackId: string
        playbackToken: string
        accentColor?: string | null
        title?: string | null
    }
    | {
        kind: 'on-demand'
        playbackId: string
        playbackToken: string
        /** Signed thumbnails need their own token; without one there is no poster rather than a refused request. */
        thumbnailToken?: string | null
        /** Where it starts, in seconds of the recording. */
        startSeconds?: number | null
        autoplay: boolean
        /** The sound off, the one way every browser lets a video start unasked (ORB-1118). */
        muted?: boolean
        accentColor?: string | null
        title?: string | null
    }

/**
 * The `<mux-player>` attributes for a source, null for one to remove. They are set on the element itself rather than
 * through JSX: Preact sets a property where the element has one, and before the element is defined it has none.
 *
 * - Live is low latency (`ll-live`), starts by itself (with sound if the browser lets it, muted if not), and takes no
 *   keys: nothing ahead exists to skip to, and nothing should pause the session by accident.
 * - A muted video on demand has the sound off whatever the browser would allow, so a viewer who never asked for sound
 *   gets none, and starts by itself only with `autoplay`.
 * - The live poster is empty: Mux's default would ask for a live thumbnail the signed stream refuses.
 * - `disable-cookies`: Mux Data still reports playback, without leaving a cookie on the client's page.
 */
export function muxPlayerAttributes(source: RoomPlayerSource): Record<string, string | null> {
    const shared = {
        'playback-id': source.playbackId,
        'playback-token': source.playbackToken,
        'accent-color': source.accentColor || null,
        'metadata-video-title': source.title || null,
        'disable-cookies': '',
    }
    if (source.kind === 'live') {
        return { ...shared, 'stream-type': 'll-live', autoplay: 'any', nohotkeys: '', poster: '', 'thumbnail-token': null, 'start-time': null, muted: null }
    }
    const start = source.startSeconds != null && source.startSeconds > 0 ? String(Math.floor(source.startSeconds)) : null
    return {
        ...shared,
        'stream-type': 'on-demand',
        autoplay: !source.autoplay ? null : source.muted ? 'muted' : 'any',
        muted: source.muted ? '' : null,
        nohotkeys: null,
        poster: source.thumbnailToken ? null : '',
        'thumbnail-token': source.thumbnailToken || null,
        'start-time': start,
    }
}

/**
 * The live stream can be played: Mux has it on the air. Reconnecting counts, because Mux keeps the stream active and
 * plays its slate through a dropped encoder, so a player already running rides the drop out. Before Live, Mux answers
 * 412 and Mux Player gives up within minutes ("Network error, try reloading"), so nothing mounts until then.
 */
export function streamOnAir(videoState: WebinarRoomVideoState): boolean {
    return videoState === 'Live' || videoState === 'Reconnecting'
}

/**
 * Which live player this is: a new one each time the stream comes on the air from off it. A stream that ended has
 * written its end into the playlist and the old player has ended with it, so a stream that comes back (Restart video
 * after the reconnect window) needs a fresh player, not the old one.
 */
export function nextLiveGeneration(previous: WebinarRoomVideoState | null, next: WebinarRoomVideoState, generation: number): number {
    return streamOnAir(next) && !(previous !== null && streamOnAir(previous)) ? generation + 1 : generation
}
